/**
 * LaunchDarkly feature flag keys and types.
 *
 * LD_FLAGS maps logical names to the raw kebab-case flag keys returned by the SDK
 * (LDProvider is configured with useCamelCaseFlagKeys: false).
 *
 * To rename a flag: update the value here — every consumer updates automatically.
 * To add a flag:    add it to LD_FLAGS + LDAppFlags below, and read it through
 *                   `useLaunchDarklyFlags()` + `resolveLaunchDarklyFlag()`.
 *
 * No flags are currently read by the app: the last two were removed in PEG-1101.
 */

export const LD_FLAGS = {} as const;

/**
 * Shape of all flags returned by useFlags().
 * Augments the SDK so useFlags() is fully typed — no casting needed in components.
 */
export type LDAppFlags = Record<never, boolean | undefined>;

declare module 'launchdarkly-react-client-sdk' {
  interface LDFlagSet extends LDAppFlags {}
}

/**
 * Server-evaluated flags for the session's org (`src/server/launchdarkly.ts`), handed to
 * the client so flags are correct on first render. `flags` is `allFlagsState().toJSON()`,
 * which the client SDK accepts as-is for `options.bootstrap`.
 */
export type LaunchDarklyBootstrap = {
  context: { kind: 'organization'; key: string };
  flags: Record<string, unknown>;
};

/** Flag values from a bootstrap payload, minus its `$flagsState` / `$valid` metadata. */
export const flagValuesFromBootstrap = (flags: Record<string, unknown>): LDAppFlags =>
  Object.fromEntries(Object.entries(flags).filter(([flagKey]) => !flagKey.startsWith('$')));

/**
 * Opt-in local bypass: skip the LaunchDarkly client and force every flag on.
 *
 * Only ever active when the engineer sets NEXT_PUBLIC_LD_LOCAL_BYPASS=true in a
 * local environment file. Left unset — including in `.env.development`, which
 * ships — local dev resolves flags through LaunchDarkly exactly like staging and
 * production, so flag-gated surfaces behave the same everywhere and the local
 * Playwright suite keeps control of which flags are on (see the stubbed evalx
 * payload in tests/e2e-local/fixtures/localTest.ts).
 */
export const isLocalLaunchDarklyBypassEnabled = (
  environment = process.env.NODE_ENV,
  optIn = process.env.NEXT_PUBLIC_LD_LOCAL_BYPASS,
): boolean => environment === 'development' && optIn === 'true';

/** A boolean flag's effective value: forced on under the local bypass, otherwise off unless set. */
export const resolveLaunchDarklyFlag = (
  value: boolean | undefined,
  environment = process.env.NODE_ENV,
): boolean => (isLocalLaunchDarklyBypassEnabled(environment) ? true : (value ?? false));
